import { Typography } from '@euler/app/components/typography';
import { HStack } from '@euler/components/layout/Stack';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { memo } from 'react';
import { View } from 'react-native';
import { StatusPalettes } from '../constants';
import { StatusPaletteKey } from '../types';

/**
 * Compact "label N" pill used to surface counts on the task card.
 * - default: tinted bg + colored text (label and count share the palette).
 * - solid:   solid dark bg with white label; count sits inside a small
 *            white "number-badge" pill.
 */
export const MetricBadge = memo(
  ({
    label,
    count,
    palette,
    solid,
  }: {
    label: string;
    count: number;
    palette: StatusPaletteKey;
    solid?: boolean;
  }) => {
    const { rpx } = useResponsiveContext();
    const { text, bg } = StatusPalettes[palette];

    if (solid) {
      return (
        <HStack
          css={`
            height: ${rpx(20)}px;
            border-radius: ${rpx(4)}px;
            padding: 0 ${rpx(4)}px 0 ${rpx(8)}px;
            background-color: ${text};
            align-items: center;
            margin-right: ${rpx(6)}px;
          `}
        >
          <Typography.Label size={rpx(11)} lineHeight={rpx(15)} color="#ffffff">
            {label}
          </Typography.Label>
          <View
            css={`
              min-width: ${rpx(16)}px;
              height: ${rpx(14)}px;
              border-radius: ${rpx(7)}px;
              background-color: #ffffff;
              padding: 0 ${rpx(4)}px;
              align-items: center;
              justify-content: center;
              margin-left: ${rpx(5)}px;
            `}
          >
            <Typography.Label
              medium
              size={rpx(10)}
              lineHeight={rpx(13)}
              color={text}
            >
              {count}
            </Typography.Label>
          </View>
        </HStack>
      );
    }

    return (
      <HStack
        css={`
          height: ${rpx(20)}px;
          border-radius: ${rpx(4)}px;
          padding: 0 ${rpx(8)}px;
          background-color: ${bg};
          align-items: center;
          margin-right: ${rpx(6)}px;
        `}
      >
        <Typography.Label size={rpx(11)} lineHeight={rpx(15)} color={text}>
          {label}
        </Typography.Label>
        <Typography.Label
          medium
          size={rpx(12)}
          lineHeight={rpx(15)}
          color={text}
          css={`
            margin-left: ${rpx(3)}px;
          `}
        >
          {count}
        </Typography.Label>
      </HStack>
    );
  },
);
